import { Router } from "express";
import { db } from "@workspace/db";
import { teamAssignmentsTable, tendersTable } from "@workspace/db";
import { eq, and } from "drizzle-orm";
import { requireAuth, type AuthRequest } from "../lib/auth";
import { broadcastTenderUpdate } from "../lib/socket";

const router = Router();

router.get("/tenders/:id/team", requireAuth, async (req: AuthRequest, res, next) => {
  try {
    if (!req.companyId) return res.status(400).json({ error: "No company profile" });
    const tenderId = Number(req.params.id);

    const tender = await db.select({ id: tendersTable.id }).from(tendersTable)
      .where(and(eq(tendersTable.id, tenderId), eq(tendersTable.companyId, req.companyId))).limit(1);
    if (!tender[0]) return res.status(404).json({ error: "Tender not found" });

    const members = await db.select().from(teamAssignmentsTable)
      .where(and(eq(teamAssignmentsTable.tenderId, tenderId), eq(teamAssignmentsTable.companyId, req.companyId)));
    res.json(members);
  } catch (err) {
    next(err);
  }
});

router.post("/tenders/:id/team", requireAuth, async (req: AuthRequest, res, next) => {
  try {
    if (!req.companyId) return res.status(400).json({ error: "No company profile" });
    const tenderId = Number(req.params.id);

    const tender = await db.select({ id: tendersTable.id }).from(tendersTable)
      .where(and(eq(tendersTable.id, tenderId), eq(tendersTable.companyId, req.companyId))).limit(1);
    if (!tender[0]) return res.status(404).json({ error: "Tender not found" });

    const { userName, userEmail, role, notes, userId } = req.body;
    const member = await db.insert(teamAssignmentsTable)
      .values({ companyId: req.companyId, tenderId, userId, userName, userEmail, role: role ?? "member", notes })
      .returning();
    broadcastTenderUpdate(tenderId, "team:update", { action: "added", member: member[0] });
    res.status(201).json(member[0]);
  } catch (err) {
    next(err);
  }
});

router.patch("/team/:memberId", requireAuth, async (req: AuthRequest, res, next) => {
  try {
    if (!req.companyId) return res.status(400).json({ error: "No company profile" });

    const existing = await db.select().from(teamAssignmentsTable)
      .where(and(eq(teamAssignmentsTable.id, Number(req.params.memberId)), eq(teamAssignmentsTable.companyId, req.companyId)))
      .limit(1);
    if (!existing[0]) return res.status(404).json({ error: "Not found" });

    const updated = await db.update(teamAssignmentsTable)
      .set({ ...req.body, updatedAt: new Date() })
      .where(eq(teamAssignmentsTable.id, Number(req.params.memberId)))
      .returning();
    broadcastTenderUpdate(existing[0].tenderId, "team:update", { action: "updated", member: updated[0] });
    res.json(updated[0]);
  } catch (err) {
    next(err);
  }
});

router.delete("/team/:memberId", requireAuth, async (req: AuthRequest, res, next) => {
  try {
    if (!req.companyId) return res.status(400).json({ error: "No company profile" });

    const existing = await db.select().from(teamAssignmentsTable)
      .where(and(eq(teamAssignmentsTable.id, Number(req.params.memberId)), eq(teamAssignmentsTable.companyId, req.companyId)))
      .limit(1);
    if (!existing[0]) return res.status(404).json({ error: "Not found" });

    await db.delete(teamAssignmentsTable).where(eq(teamAssignmentsTable.id, Number(req.params.memberId)));
    broadcastTenderUpdate(existing[0].tenderId, "team:update", { action: "removed", memberId: Number(req.params.memberId) });
    res.status(204).send();
  } catch (err) {
    next(err);
  }
});

export default router;
